'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { range } from '@/utils';
import useIsOnscreen from '@/hooks/use-is-onscreen';

import Demo, { DemoSliderControl } from '@/components/Demo';
import { COLORS } from './constants';

function ColumnRipple({ initialDelay = 0, includeChildren }) {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  const ref = React.useRef();
  const isOnscreen = useIsOnscreen(ref);

  const height = includeChildren ? 325 : 275;

  return (
    <Demo
      id="column-ripple"
      ref={ref}
      initialValues={{
        delay: initialDelay,
      }}
      outputWrapper={OutputWrapper}
      customGrid={{
        gridTemplateColumns: 'min(400px, 100%)',
        justifyContent: 'center',
      }}
      controls={[
        <DemoSliderControl
          key="delay"
          id="delay"
          label="Staggered Delay"
          previewRenderer={(val) => {
            if (val === 0) {
              return '0ms (default)';
            }

            return val + 'ms';
          }}
          min={0}
          max={260}
        />,
      ]}
    >
      {({ delay }) => (
        <Wrapper
          onMouseLeave={() => setHoveredIndex(null)}
          style={{ height }}
        >
          <WrapperLabel>.wrapper</WrapperLabel>
          {isOnscreen &&
            range(6).map((chunkIndex) => (
              <Column
                key={chunkIndex}
                style={{
                  animationDelay: chunkIndex * delay + 'ms',
                  opacity:
                    typeof hoveredIndex === 'number' &&
                    hoveredIndex !== chunkIndex
                      ? 0.25
                      : 1,
                }}
                onMouseEnter={() => setHoveredIndex(chunkIndex)}
              >
                <ColumnLabel>.column</ColumnLabel>
                {includeChildren &&
                  COLORS.rainbow.map((color, index) => (
                    <Cell
                      key={color}
                      style={{
                        background: color,
                        color: index > 1 && index < 6 && 'black',
                      }}
                    >
                      .cell
                    </Cell>
                  ))}
              </Column>
            ))}
        </Wrapper>
      )}
    </Demo>
  );
}

const OutputWrapper = styled.div`
  background: white;
  padding: 52px 16px;
  display: flex;
  justify-content: center;
`;

const Wrapper = styled.div`
  --big-gap: 4px;
  --small-gap: 2px;
  --billow: 16px;
  position: relative;
  max-width: 450px;
  height: 274px;
  display: flex;
  gap: var(--small-gap);
  width: 100%;
  padding: var(--big-gap);
  border: 3px solid black;
`;
const Label = styled.span`
  position: absolute;
  top: 0;
  height: 2rem;
  background: black;
  color: white;
  line-height: 2rem;
  font-size: 1rem;
`;
const WrapperLabel = styled(Label)`
  right: -3px;
  font-size: 1rem;
  padding: 0 16px;

  transform: translateY(-100%);
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--small-gap);
  height: 100%;
  padding: var(--big-gap);
  border: 3px solid black;
  outline: 2px solid white;
  background: white;
  animation: oscillate 500ms alternate infinite ease-in-out both;
  transition: opacity 200ms;
  will-change: transform;
  cursor: default;

  @keyframes oscillate {
    from {
      transform: translateY(var(--billow));
    }
    to {
      transform: translateY(calc(var(--billow) * -1));
    }
  }

  @media (max-width: 32.5rem) {
    &:nth-child(n + 7) {
      display: none;
    }
  }
  @media (max-width: 26.25rem) {
    &:nth-child(n + 6) {
      display: none;
    }
  }
  @media (max-width: 21.875rem) {
    &:nth-child(n + 5) {
      display: none;
    }
  }
`;

const ColumnLabel = styled(Label)`
  position: relative;
  text-align: center;
  font-size: 0.875rem;
  margin-top: calc(var(--big-gap) * -1);
  margin-left: calc(var(--big-gap) * -1);
  margin-right: calc(var(--big-gap) * -1);
  margin-bottom: var(--small-gap);
`;

const Cell = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;
  border: 2px solid black;
`;

export default ColumnRipple;
